import INDUSTRY_OPTIONS from '../../constants/industryConstants';
import {
  FIVE_AM_IN_MIN,
  NINE_THIRTY_PM_IN_MIN,
  ONE_THIRTY_PM_IN_MIN,
  SHIFT_OPTION_KEYS,
} from '../../constants/jobOrderConstants';
import { getTimeZone } from '../../utils';

export const SORT_VALUES = {
  ASC: 'asc',
  DESC: 'desc',
};

export const INITIAL_FILTER_SORT_PAGE = {
  filters: [],
  pageSize: 10,
  page: 0,
  sortBy: [],
};

export const SEARCH_PARAM_KEYS = {
  ROLE: 'role',
  STATUS: 'status',
  STATUS_V2: 'statusV2',
  NAME: 'name',
  EMAIL: 'email',
  PHONE: 'phone',
  SSN_LAST4: 'ssnLast4',
  LOGIN_SORT: 'loginsort',
  AGENCY: 'agency',
  RECRUITER: 'recruiter',
  NO_FUTURE_WORK: 'noFutureWork',
  INDUSTRY_MEETS_PREF: 'industryMeetsPref',
  PAY_RATE_MEETS_PREF: 'payRateMeetsPref',
  WOTC_ELIGIBLE: 'wotcEligible',
  URBAN_YOUTH_CERTIFIED: 'urbanYouthCertified',
  SHIFT_START: 'shiftStart',
  SHIFT_END: 'shiftEnd',
  DISTANCE_LAT: 'distanceLat',
  DISTANCE_LNG: 'distanceLng',
  DISTANCE_MILES: 'distanceMiles',
  DISTANCE_CITY: 'distanceCity',
  DISTANCE_STATE: 'distanceState',
  DISTANCE_LOCATION: 'distanceLocation',
  DISTANCE_RADIUS_TYPE: 'distanceRadiusType',
};

export const DISTANCE_SEARCH_PARAM_KEYS = [
  SEARCH_PARAM_KEYS.DISTANCE_LAT,
  SEARCH_PARAM_KEYS.DISTANCE_LNG,
  SEARCH_PARAM_KEYS.DISTANCE_MILES,
  SEARCH_PARAM_KEYS.DISTANCE_CITY,
  SEARCH_PARAM_KEYS.DISTANCE_STATE,
  SEARCH_PARAM_KEYS.DISTANCE_LOCATION,
  SEARCH_PARAM_KEYS.DISTANCE_RADIUS_TYPE,
];

export const INDUSTRY_FILTER_OPTIONS = Object.values(INDUSTRY_OPTIONS).map(
  (industry) => industry.charAt(0).toUpperCase() + industry.slice(1)
);

export const PAY_RATE_FILTER_OPTIONS = Array.from({ length: 94 }, (_, i) => {
  const rate = i + 7;
  return { value: String(rate), name: String(rate) };
});

export const SHIFT_TIME_FILTER_OPTIONS = Object.values(SHIFT_OPTION_KEYS);

export const SHIFT_OPTIONS_PAYLOAD = {
  [SHIFT_OPTION_KEYS.FIVE_AM_TO_ONE_THIRTY_PM]: {
    field: 'shift',
    operation: 'betweenClockTimes',
    value: { start: FIVE_AM_IN_MIN, end: ONE_THIRTY_PM_IN_MIN, zone: getTimeZone() },
  },
  [SHIFT_OPTION_KEYS.ONE_THIRTY_PM_TO_NINE_THIRTY_PM]: {
    field: 'shift',
    operation: 'betweenClockTimes',
    value: { start: ONE_THIRTY_PM_IN_MIN, end: NINE_THIRTY_PM_IN_MIN, zone: getTimeZone() },
  },
  [SHIFT_OPTION_KEYS.NINE_THIRTY_PM_TO_FIVE_AM]: {
    field: 'shift',
    operation: 'betweenClockTimes',
    value: { start: NINE_THIRTY_PM_IN_MIN, end: FIVE_AM_IN_MIN, zone: getTimeZone() },
  },
};

const parseCsvFilterValues = (paramValue) => {
  if (!paramValue) return null;
  const values = paramValue
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
  return values.length ? values : null;
};

const parseCsvIndustryFilterValues = (paramValue) => {
  const values = parseCsvFilterValues(paramValue);
  if (!values) return null;
  return values.map((value) => value.toLowerCase());
};

const parsePayRateFilterValue = (paramValue) => {
  if (!paramValue) return null;
  const parsed = Number(paramValue);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseNoFutureWorkFilterValue = (paramValue) => {
  if (!paramValue) return null;
  return 0;
};

const parsePhoneFilterValue = (paramValue) => {
  if (!paramValue) return null;
  const digits = paramValue.replace(/\D/g, '');
  return digits || null;
};

export const normalizeSsnLast4Input = (value) => (value || '').replace(/\D/g, '').slice(-4);

export const commitSsnLast4SearchValue = (value) => {
  const digits = normalizeSsnLast4Input(value);
  if (digits.length !== 4) return '';
  return digits;
};

const parseSsnLast4FilterValue = (paramValue) => commitSsnLast4SearchValue(paramValue) || null;

const parseTrueFlagFilterValue = (paramValue) => (paramValue === 'true' ? true : null);

export const FILTER_FIELDS_TO_OPERATION = {
  status: { field: 'status', operation: 'equals' },
  statusV2: { field: 'state.statusV2.status', operation: 'equals' },
  role: { field: 'role', operation: 'equals' },
  name: { field: 'name', operation: 'icontains' },
  email: { field: 'email', operation: 'icontains' },
  phone: { field: 'phone.phoneNumber', operation: 'icontains' },
  ssnLast4: { field: 'ssnLast4', operation: 'equals' },
  agency: { field: 'candidate.agency.ID', operation: 'equalsID' },
  recruiter: { field: 'candidate.recruiter.ID', operation: 'equalsID' },
  // TODO: This filter only selects candidates who work at the agency. A new filter would be needed to simultaneously filter for recruiters who work at the agency.
  noFutureWork: { field: 'futureScheduledHours', operation: 'equals' },
  industryMeetsPref: { field: 'candidate.industry', operation: 'includedIn' },
  payRateMeetsPref: { field: 'candidate.minPay', operation: 'lessThanEqual' },
  shift: { field: 'candidate.preferredShiftTimes', operation: 'equals' },
  wotcEligible: {
    field: 'candidate.taxCreditOpportunities.wotc.isEligible',
    operation: 'equals',
  },
  urbanYouthCertified: {
    field: 'candidate.taxCreditOpportunities.nyUrbanYouth.isCertified',
    operation: 'equals',
  },
};

export const STRIKE_TYPE = {
  SUSPENSION: 'suspension',
  INFRACTION: 'infraction',
};

export const FILTER_MAPPING = {
  [SEARCH_PARAM_KEYS.STATUS]: {
    field: FILTER_FIELDS_TO_OPERATION.status.field,
    operation: FILTER_FIELDS_TO_OPERATION.status.operation,
  },
  [SEARCH_PARAM_KEYS.STATUS_V2]: {
    field: FILTER_FIELDS_TO_OPERATION.statusV2.field,
    operation: FILTER_FIELDS_TO_OPERATION.statusV2.operation,
  },
  [SEARCH_PARAM_KEYS.ROLE]: {
    field: FILTER_FIELDS_TO_OPERATION.role.field,
    operation: FILTER_FIELDS_TO_OPERATION.role.operation,
  },
  [SEARCH_PARAM_KEYS.NAME]: {
    field: FILTER_FIELDS_TO_OPERATION.name.field,
    operation: FILTER_FIELDS_TO_OPERATION.name.operation,
  },
  [SEARCH_PARAM_KEYS.EMAIL]: {
    field: FILTER_FIELDS_TO_OPERATION.email.field,
    operation: FILTER_FIELDS_TO_OPERATION.email.operation,
  },
  [SEARCH_PARAM_KEYS.PHONE]: {
    field: FILTER_FIELDS_TO_OPERATION.phone.field,
    operation: FILTER_FIELDS_TO_OPERATION.phone.operation,
    convertValue: parsePhoneFilterValue,
  },
  [SEARCH_PARAM_KEYS.SSN_LAST4]: {
    field: FILTER_FIELDS_TO_OPERATION.ssnLast4.field,
    operation: FILTER_FIELDS_TO_OPERATION.ssnLast4.operation,
    convertValue: parseSsnLast4FilterValue,
  },
  [SEARCH_PARAM_KEYS.AGENCY]: {
    field: FILTER_FIELDS_TO_OPERATION.agency.field,
    operation: FILTER_FIELDS_TO_OPERATION.agency.operation,
  },
  [SEARCH_PARAM_KEYS.RECRUITER]: {
    field: FILTER_FIELDS_TO_OPERATION.recruiter.field,
    operation: FILTER_FIELDS_TO_OPERATION.recruiter.operation,
  },
  [SEARCH_PARAM_KEYS.NO_FUTURE_WORK]: {
    field: FILTER_FIELDS_TO_OPERATION.noFutureWork.field,
    operation: FILTER_FIELDS_TO_OPERATION.noFutureWork.operation,
    convertValue: parseNoFutureWorkFilterValue,
  },
  [SEARCH_PARAM_KEYS.INDUSTRY_MEETS_PREF]: {
    field: FILTER_FIELDS_TO_OPERATION.industryMeetsPref.field,
    operation: FILTER_FIELDS_TO_OPERATION.industryMeetsPref.operation,
    convertValue: parseCsvIndustryFilterValues,
  },
  [SEARCH_PARAM_KEYS.PAY_RATE_MEETS_PREF]: {
    field: FILTER_FIELDS_TO_OPERATION.payRateMeetsPref.field,
    operation: FILTER_FIELDS_TO_OPERATION.payRateMeetsPref.operation,
    convertValue: parsePayRateFilterValue,
  },
  [SEARCH_PARAM_KEYS.WOTC_ELIGIBLE]: {
    field: FILTER_FIELDS_TO_OPERATION.wotcEligible.field,
    operation: FILTER_FIELDS_TO_OPERATION.wotcEligible.operation,
    convertValue: parseTrueFlagFilterValue,
  },
  [SEARCH_PARAM_KEYS.URBAN_YOUTH_CERTIFIED]: {
    field: FILTER_FIELDS_TO_OPERATION.urbanYouthCertified.field,
    operation: FILTER_FIELDS_TO_OPERATION.urbanYouthCertified.operation,
    convertValue: parseTrueFlagFilterValue,
  },
};

export const SORT_MAPPING = {
  [SEARCH_PARAM_KEYS.LOGIN_SORT]: {
    field: 'lastLoginDate',
    getIsDescending: (v) => v === SORT_VALUES.DESC,
  },
};

export const ROLE_OPTIONS = [
  {
    value: 'candidate',
    name: 'Candidate',
    nameKey: 'userRole.worker',
    headerKey: 'userList.header.workers',
  },
  {
    value: 'recruiter',
    name: 'Recruiter',
    nameKey: 'userRole.recruiter',
    headerKey: 'userList.header.recruiters',
  },
  {
    value: 'employer',
    name: 'Employer',
    nameKey: 'userRole.employer',
    headerKey: 'userList.header.employers',
  },
  {
    value: 'admin',
    name: 'Admin',
    nameKey: 'userRole.admin',
    headerKey: 'userList.header.admins',
  },
];

export const STATUS_OPTIONS = [
  { value: 'active', name: 'Active', nameKey: 'user.status.active' },
  { value: 'deactivated', name: 'Deactivated', nameKey: 'user.status.deactivated' },
  { value: 'DNA', name: 'DNA', nameKey: 'user.status.DNA' },
  {
    value: 'onboardingIncomplete',
    name: 'Currently Onboarding',
    nameKey: 'user.status.onboardingIncomplete.full',
  },
  { value: 'inactive', name: 'Inactive', nameKey: 'user.status.inactive' },
  { value: 'suspended', name: 'Suspended', nameKey: 'user.status.suspended' },
  { value: 'unverified', name: 'Unverified', nameKey: 'user.status.unverified' },
];

export const TIME = {
  AM: 'am',
  PM: 'pm',
};

export const INFRACTION_TYPE = {
  NCNS: 'ncns',
  ABSENT: 'absent',
  LATE: 'late',
};

export const MAX_STRIKES = 3;
export const THIRTY_DAYS_IN_MS = 2592000000;
export const TWENTY_FIVE_DAYS_IN_MS = 2160000000;
export const USER_STATUS = {
  ACTIVE: 'active',
  DEACTIVATED: 'deactivated',
  DNA: 'DNA',
  INACTIVE: 'inactive',
  ONBOARDING_INCOMPLETE: 'onboardingIncomplete',
  SUSPENDED: 'suspended',
  UNVERIFIED: 'unverified',
};

export const FILTERS = {
  UPCOMING: { field: 'end', operation: 'after', value: +new Date() },
  COMPLETED: { field: 'end', operation: 'before', value: +new Date() },
};

export const SUSPENSION_NUM = {
  FIRST: 'userDetail.attendance.firstSuspension',
  SECOND: 'userDetail.attendance.secondSuspension',
  THIRD: 'userDetail.attendance.thirdSuspension',
};

export const INFRACTION_DISPLAY = {
  absent: 'userDetail.attendance.absent',
  ncns: 'userDetail.attendance.ncns',
};

export const INFRACTION_SUBTEXT = {
  absent: 'userDetail.attendance.absent.subtext',
  ncns: 'userDetail.attendance.ncns.subtext',
};

export const PENDING_PLACEMENT_STATUS = {
  INVITE: 'pendingEmployeeAcceptance',
  APPLICATION: 'pendingEmployerApproval',
};
